import React, { FC, ReactNode, useEffect, useState, useRef, ComponentPropsWithRef, PropsWithChildren } from 'react';

import { isBrowser } from '@lucid/utils';
import { variant, mqMin } from '@lucid/theme';
import styled from '@emotion/styled';
import gsap from 'gsap';

import type { LucidTheme, Component } from '../../../types/types';
import Box from '../../box/box';
import Icon from '../../icon/icon';
import Link, { LinkProps } from '../../link/link';
import Text from '../../text/text';

export interface AlertBannerVariantProps extends ComponentPropsWithRef<'div'> {
  /** The children to be rendered. */
  children?: ReactNode;
  /** The available variants for the Alert Banner Component. */
  variant?: Component.Type['AlertBanner'];
}

export interface AlertBannerProps extends AlertBannerVariantProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The children to be rendered. */
  children?: ReactNode;
  /** The condition on which to show the AlertBanner. */
  hasCondition?: boolean;
  /** Allows the ability to pass in props for the Link component. */
  linkProps?: LinkProps;
  /** The storage key to be saved in the browser localStorage. */
  storageKey: string;
  /** Allows the ability to choose localStorage or sessionStorage for the Alert Component. */
  storageType?: 'local' | 'session';
  /** Hides the Alert Banner when link has been clicked. */
  willDismissOnLinkClick?: boolean;
}

const StyledAlertBanner: FC<PropsWithChildren<AlertBannerVariantProps>> = styled.div(
  variant({
    scale: 'alert.banner',
    variants: {
      defaultVariant: {},
    },
  }),
  ({ theme }) => ({
    label: 'alertBanner',
    position: 'sticky',
    bottom: 0,
    left: 0,
    right: 0,
    width: '100%',
    zIndex: theme.zIndices[3],
  }),
);

const StyledAlertBannerButton: FC<PropsWithChildren<AlertBannerVariantProps & { onClick?: () => void }>> =
  styled.button(
    variant({
      scale: 'alert.button',
      variants: {
        defaultVariant: {},
      },
    }),
    ({ theme }) => ({
      label: 'alertBannerButton',
      background: 'none',
      cursor: 'pointer',
      height: '35px',
      width: '35px',
      fontSize: theme.fontSizes[7],
      fontWeight: theme.fontWeights.light,
      lineHeight: 0,
    }),
  );

const AlertBanner: FC<PropsWithChildren<AlertBannerProps>> = ({
  children,
  className,
  hasCondition = true,
  willDismissOnLinkClick = true,
  linkProps,
  storageKey,
  storageType = 'session',
  variant = 'white',
}) => {
  const [willClose, setWillClose] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const isSession = storageType === 'session';
  const isLocal = storageType === 'local';

  const animationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const getStorageItem = (currentStorageKey: string) => {
      if (isLocal) return window.localStorage.getItem(currentStorageKey);

      if (isSession) return window.sessionStorage.getItem(currentStorageKey);
    };

    const setStorageItem = (currentStorageKey: string, storageValue: 'closed') => {
      if (isLocal) return window.localStorage.setItem(currentStorageKey, storageValue);

      if (isSession) return window.sessionStorage.setItem(currentStorageKey, storageValue);
    };

    if (isBrowser()) {
      const sessionValue = getStorageItem(storageKey);

      setWillClose(!!sessionValue);

      if (!sessionValue) {
        setIsVisible(true);
      }

      if (willClose) {
        setStorageItem(storageKey, 'closed');
      }
    }
  }, [willClose, storageKey, isLocal, isSession]);

  useEffect(() => {
    if (animationRef.current && isBrowser()) {
      gsap
        .timeline({ delay: 0.5 })
        .from(animationRef.current, { y: '-20', duration: 0.667 }, 0)
        .from(animationRef.current, { opacity: 0, duration: 0.5 }, 0.167);
    }
  }, [isVisible]);

  if (!hasCondition || !isVisible || willClose) {
    return null;
  }

  const handleOnClick = () => {
    if (willDismissOnLinkClick) {
      return setWillClose(true);
    }

    return setWillClose(false);
  };

  return (
    <StyledAlertBanner ref={animationRef} className={className} data-testid="alertBanner" variant={variant}>
      <Box
        alignItems="baseline"
        css={(theme: LucidTheme) => ({
          marginRight: theme.space[4],
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          justifyContent: 'center',
          gap: theme.space.s16,
          [mqMin.tablet]: {
            flexDirection: 'row',
            alignItems: 'center',
          },
        })}
      >
        <Text
          css={{
            marginBottom: 0,
            color: 'inherit',
          }}
          variant="paragraphS400"
        >
          {children}
        </Text>
        {linkProps?.to && (
          <Link
            {...linkProps}
            css={{
              whiteSpace: 'nowrap',
            }}
            onClick={handleOnClick}
          />
        )}
      </Box>
      <StyledAlertBannerButton
        aria-label="Close alert"
        data-testid="alertBannerCloseButton"
        onClick={handleOnClick}
        variant={variant}
      >
        <Icon sizing="responsive" type="X" />
      </StyledAlertBannerButton>
    </StyledAlertBanner>
  );
};

export { AlertBanner };
export default AlertBanner;
